import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Pencil, Zap, Rocket, Send, Play, Trash2, Power } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/shared/DataTable';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { useToast } from '@/components/shared/Toast';
import { JsonField, tryParseJson } from '@/components/agents/JsonField';
import { CodeEditor } from '@/components/webhooks/CodeEditor';
import {
  useGetAgentFactoryQuery,
  useGetAgentModelCollectionsQuery,
  useGetAgentTriggersQuery,
  useUpdateAgentTriggerMutation,
  useDeleteAgentTriggerMutation,
} from '@/store/api/agentFactoryApi';
import {
  useGetConversationsQuery,
  useCreateConversationMutation,
} from '@/store/api/agentRuntimeApi';
import type { Conversation, TriggerBinding } from '@/types';

function MetadataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="text-sm">{value}</div>
    </div>
  );
}

export function FactoryDetailPage() {
  const { factoryId } = useParams<{ factoryId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const id = factoryId ?? '';
  const { data: factory, isLoading, isError } = useGetAgentFactoryQuery(id, { skip: !id });
  const { data: collections } = useGetAgentModelCollectionsQuery();
  const collectionById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of collections ?? []) map.set(c._id, c.name);
    return map;
  }, [collections]);

  const { data: triggers } = useGetAgentTriggersQuery({ factoryId: id });
  const { data: conversationsResult } = useGetConversationsQuery(
    { factoryId: id, limit: 100 },
    { skip: !id },
  );
  const factoryConversations = conversationsResult?.data ?? [];

  const [createConversation, { isLoading: dispatching }] = useCreateConversationMutation();
  const [updateTrigger, { isLoading: toggling }] = useUpdateAgentTriggerMutation();
  const [deleteTrigger, { isLoading: deleting }] = useDeleteAgentTriggerMutation();

  const [dispatchJson, setDispatchJson] = useState('');
  const [dispatchName, setDispatchName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TriggerBinding | null>(null);

  // Reset dispatch payload when the factory changes.
  useEffect(() => {
    setDispatchJson('');
    setDispatchName('');
  }, [id]);

  const toggleTrigger = async (t: TriggerBinding) => {
    try {
      await updateTrigger({ id: t._id, body: { isActive: !t.isActive } }).unwrap();
      toastSuccess(t.isActive ? 'Trigger disabled' : 'Trigger enabled');
    } catch {
      toastError('Failed to update trigger');
    }
  };

  const handleDeleteTrigger = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTrigger(deleteTarget._id).unwrap();
      toastSuccess('Trigger deleted');
      setDeleteTarget(null);
    } catch {
      toastError('Failed to delete trigger');
    }
  };

  const handleDispatch = async () => {
    let payload: Record<string, unknown> | undefined;
    if (dispatchJson.trim()) {
      try {
        payload = tryParseJson(dispatchJson) as Record<string, unknown>;
      } catch {
        toastError('Dispatch payload is not valid JSON');
        return;
      }
    }
    try {
      const result = await createConversation({
        factoryId: id,
        name: dispatchName.trim() || undefined,
        payload,
      }).unwrap();
      const conversationId =
        (result as any)?.conversationId ?? (result as any)?.data?._id ?? (result as any)?.data?.id;
      toastSuccess('Conversation dispatched');
      if (conversationId) navigate(`/conversations/${conversationId}`);
    } catch (err) {
      toastError((err as any)?.data?.message || 'Failed to dispatch conversation');
    }
  };

  const conversationColumns: ColumnDef<Conversation, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className="truncate font-medium">
            {row.original.name ?? row.original._id}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <Badge variant="outline">{row.original.status}</Badge>,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, HH:mm'),
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !factory) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/factories')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <p className="pt-8 text-center text-destructive">Factory not found or failed to load.</p>
      </div>
    );
  }

  const structuredEnabled = factory.structuredOutput?.enabled;
  const bootstrapEnabled = factory.bootstrap?.enabled;
  const compactionEnabled = factory.compaction?.enabled;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/factories')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <span className="truncate">{factory.name}</span>
          </h1>
          {factory.description && (
            <p className="truncate text-sm text-muted-foreground">{factory.description}</p>
          )}
        </div>
        <Button variant="outline" onClick={() => navigate(`/factories/${id}/edit`)} className="gap-1.5">
          <Pencil className="h-4 w-4" /> Edit
        </Button>
      </div>

      {/* Config summary */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <MetadataRow
                label="Model collection"
                value={collectionById.get(factory.modelCollectionId) ?? factory.modelCollectionId}
              />
              <MetadataRow
                label="Policies"
                value={
                  factory.policyIds?.length > 0 ? factory.policyIds.length : <span>—</span>
                }
              />
              <MetadataRow label="Version" value={factory.version ?? 1} />
              <MetadataRow
                label="Structured output"
                value={structuredEnabled ? <Badge variant="success">enabled</Badge> : <Badge variant="outline">off</Badge>}
              />
              <MetadataRow
                label="Bootstrap"
                value={bootstrapEnabled ? <Badge variant="success">enabled</Badge> : <Badge variant="outline">off</Badge>}
              />
              <MetadataRow
                label="Compaction"
                value={compactionEnabled ? <Badge variant="success">enabled</Badge> : <Badge variant="outline">off</Badge>}
              />
            </div>
            <Separator />
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Instructions (markdown)</p>
              <div className="overflow-hidden rounded-md border">
                <CodeEditor
                  language="markdown"
                  value={factory.instructions}
                  readOnly
                  height={200}
                />
              </div>
            </div>
            {factory.policyIds && factory.policyIds.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {factory.policyIds.map((p) => (
                  <span key={p} className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{p}</span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dispatch panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Rocket className="h-4 w-4 text-muted-foreground" /> Dispatch
            </CardTitle>
            <CardDescription>
              Start a new conversation from this factory with an optional payload.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="dispatch-name">Conversation name</Label>
              <input
                id="dispatch-name"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={dispatchName}
                onChange={(e) => setDispatchName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <JsonField
              label="Payload (JSON)"
              value={dispatchJson}
              onChange={setDispatchJson}
              height={160}
              hint="Passed as the conversation payload to the factory."
            />
            <Button
              className="w-full gap-1.5"
              onClick={handleDispatch}
              disabled={dispatching}
            >
              <Send className="h-4 w-4" />
              {dispatching ? 'Dispatching…' : 'Dispatch'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Triggers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="h-4 w-4 text-muted-foreground" /> Triggers (AF-4)
          </CardTitle>
          <CardDescription>Start conversations on a schedule, webhook, or internal event.</CardDescription>
        </CardHeader>
        <CardContent>
          {!triggers || triggers.length === 0 ? (
            <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
              No triggers — add one from the factory edit form (Triggers tab).
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {triggers.map((t) => (
                <div key={t._id} className="flex flex-wrap items-center gap-2 rounded-md border p-3">
                  <Badge variant="outline">{t.kind}</Badge>
                  {t.kind === 'cron' && <span className="font-mono text-xs">{t.cron}</span>}
                  {t.kind === 'webhook' && (
                    <span className="font-mono text-xs text-muted-foreground">{t.webhookId}</span>
                  )}
                  {t.kind === 'event' && (
                    <span className="text-xs text-muted-foreground">
                      {(t.eventFilter ?? []).map((f) => `${f.channel}`).join(', ')}
                    </span>
                  )}
                  <Badge variant={t.isActive ? 'success' : 'secondary'}>
                    {t.isActive ? 'active' : 'disabled'}
                  </Badge>
                  <div className="ml-auto flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t.isActive ? 'Disable' : 'Enable'}
                      disabled={toggling}
                      onClick={() => toggleTrigger(t)}
                    >
                      <Power className={t.isActive ? 'text-green-600' : 'text-muted-foreground'} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Delete"
                      className="text-destructive"
                      disabled={deleting}
                      onClick={() => setDeleteTarget(t)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversations filtered by factory */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Play className="h-4 w-4 text-muted-foreground" /> Conversations
          </CardTitle>
          <CardDescription>Recent conversations created from this factory.</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={conversationColumns}
            data={factoryConversations}
            onRowClick={(c) => navigate(`/conversations/${c._id}`)}
          />
        </CardContent>
      </Card>

      {/* Delete trigger confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete trigger</DialogTitle>
            <DialogDescription>This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteTrigger} disabled={deleting}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}